import { faSackDollar } from '@fortawesome/pro-regular-svg-icons'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  isTaxCalculationOutput,
  UserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import {
  FILING_STATUSES,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'

import { centsToCurrency } from '@/utils/currencyHelpers'
import {
  ProjectedAnnualTaxBillTooltip,
  ProjectedFederalIncomeTaxTooltip,
  PercentageDueThisQuarterTooltip,
  QuarterlyYTDTaxBillTooltip,
  ScorpSelfEmploymentTooltip,
  SpropSelfEmploymentTooltip,
} from '../../EstimateBreakdownV3Tooltips'

import { centsToDollarsOrDashes } from '../../utils'
import { useBreakdownTransparency } from '../../useBreakdownTransparency'

import { AccordionHeader } from '../shared/accordion-header'
import { BreakdownTooltip } from '../shared/breakdown-tooltip'
import { CalculationRow } from '../shared/calculation-row'

import { TextColors, TypographyStyles } from '@/styles'
import { Accordion, Card, Icon, Text, Badge, CardColors, Separator } from '@/ui'

const isScorpCalc = (taxCalc: UserTaxCalculation | undefined) =>
  taxCalc?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s

const doesCalcHaveSpouse = (taxCalc: UserTaxCalculation | undefined) =>
  taxCalc?.filingStatus &&
  [
    FILING_STATUSES.married_filing_jointly,
    FILING_STATUSES.qualifying_widow,
  ].includes(taxCalc.filingStatus)

interface TaxesSectionProps {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  isSample?: boolean
}

export const TaxesSection = ({
  taxCalc,
  estimateDetails,
  isSample,
}: TaxesSectionProps) => {
  const taxCalculationOutput = taxCalc?.taxCalculationOutput
  const isScorp = isScorpCalc(taxCalc)
  const hasSpouse = doesCalcHaveSpouse(taxCalc)
  const quarter = estimateDetails?.taxQuarter
  const transparencyEnabled = useBreakdownTransparency()

  if (
    !isTaxCalculationOutput(taxCalculationOutput) &&
    taxCalculationOutput !== undefined
  ) {
    return null
  }

  return (
    <>
      <div className="flex items-center">
        <Text variant={TypographyStyles.H2} className="flex items-center">
          <Icon icon={faSackDollar} className="mr-2" />
          Taxes
        </Text>
        {isSample && (
          <Badge color="orange" className="ml-5">
            Sample Data
          </Badge>
        )}
      </div>

      <Text color={TextColors.MUTED} className="w-full md:w-1/2">
        {isScorp
          ? "Based on your total taxable income and the IRS federal income tax rates, this is how much we're estimating you'll owe in taxes for the year."
          : "Based on your total taxable income, the IRS federal income tax rates and self-employment tax, this is how much we're estimating you'll owe in taxes for the year."}
      </Text>
      <div className="mt-4">
        <Accordion variant="text" type="single" collapsible>
          <Accordion.Item value="annual-tax-bill">
            <Accordion.Trigger className="w-full">
              <AccordionHeader
                left={
                  <BreakdownTooltip
                    trigger="Projected Annual Tax Bill"
                    content={<ProjectedAnnualTaxBillTooltip />}
                  />
                }
                right={
                  <Text variant={TypographyStyles.H3}>
                    {centsToDollarsOrDashes(
                      centsToCurrency(
                        taxCalculationOutput?.data
                          .annualFederalIncomeTaxLiabilityInCents
                      ).add(
                        taxCalculationOutput?.data
                          .annualSelfEmploymentTaxLiabilityInCents || 0
                      )
                    )}
                  </Text>
                }
              />
            </Accordion.Trigger>
            <Accordion.Content>
              <Card color={CardColors.LEVEL_1} className="p-4">
                <div className="space-y-4 p-4">
                  <CalculationRow
                    left={
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        tax
                      </Text>
                    }
                    right={
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                        className="text-right"
                      >
                        projected tax bill
                      </Text>
                    }
                    inCard
                  />

                  <CalculationRow
                    left={
                      <BreakdownTooltip
                        trigger="Projected federal income tax"
                        content={<ProjectedFederalIncomeTaxTooltip />}
                      />
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalculationOutput?.data
                            .annualFederalIncomeTaxLiabilityInCents
                        )}
                      </Text>
                    }
                    inCard
                  />

                  {(!isScorp || hasSpouse) && (
                    <CalculationRow
                      left={
                        <BreakdownTooltip
                          trigger={
                            transparencyEnabled && isScorp && hasSpouse
                              ? "Spouse's self-employment tax"
                              : 'Self-employment tax'
                          }
                          content={
                            isScorp ? (
                              <ScorpSelfEmploymentTooltip />
                            ) : (
                              <SpropSelfEmploymentTooltip />
                            )
                          }
                        />
                      }
                      right={
                        <Text variant={TypographyStyles.H3}>
                          {centsToDollarsOrDashes(
                            taxCalculationOutput?.data
                              .annualSelfEmploymentTaxLiabilityInCents
                          )}
                        </Text>
                      }
                      inCard
                    />
                  )}
                </div>
              </Card>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>

      <CalculationRow
        left={
          <BreakdownTooltip
            trigger="Percentage Due This Quarter"
            content={<PercentageDueThisQuarterTooltip />}
          />
        }
        right={
          <Text variant={TypographyStyles.H3} textAlign="right">
            {Number(quarter) * 25}%
          </Text>
        }
      />
      <Text color={TextColors.MUTED} className="w-full md:w-2/3 italic">
        {transparencyEnabled ? (
          <>
            {Number(quarter) * 25}% is your total due through Q{quarter}, not
            this quarter&apos;s share on its own — an earlier quarter that went
            unpaid rolls forward into this one.
          </>
        ) : (
          <>
            This is the percentage of your projected annual tax bill that&apos;s
            due this quarter, including any previous payments.
          </>
        )}
      </Text>

      <Separator />

      <CalculationRow
        left={
          <BreakdownTooltip
            trigger={
              <Text variant={TypographyStyles.H3}>
                Q{quarter} YTD Taxes Due
              </Text>
            }
            content={<QuarterlyYTDTaxBillTooltip />}
          />
        }
        right={
          <Text variant={TypographyStyles.H3} textAlign="right">
            {centsToDollarsOrDashes(
              taxCalculationOutput?.data.YTDGrossFederalTaxLiabilityInCents
            )}
          </Text>
        }
      />
    </>
  )
}
